import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { decodeVin, isValidVin } from '@/lib/vin'

export type VehicleFormSeed = {
  id?: string
  customer_id?: string | null
  year?: number | null
  make?: string | null
  model?: string | null
  trim?: string | null
  engine?: string | null
  drivetrain?: string | null
  body?: string | null
  color?: string | null
  vin?: string | null
  plate?: string | null
  mileage?: number | null
}

type CustomerOption = {
  id: string
  name: string
}

type SaveResponse = { id: string }

// Min input height for native <select>: matches Input.tsx h-10 (40px)
// so the dialog grid lines up.
const SELECT_CLASS =
  'h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'

export function VehicleDialog({
  open,
  onOpenChange,
  seed,
  onSaved,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  seed?: VehicleFormSeed | null
  onSaved?: (id: string) => void
}) {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  const editing = !!seed?.id

  const [customerId, setCustomerId] = useState<string>('')
  const [vin, setVin] = useState('')
  const [year, setYear] = useState<string>('')
  const [make, setMake] = useState('')
  const [model, setModel] = useState('')
  const [trim, setTrim] = useState('')
  const [engine, setEngine] = useState('')
  const [drivetrain, setDrivetrain] = useState('')
  const [body, setBody] = useState('')
  const [color, setColor] = useState('')
  const [plate, setPlate] = useState('')
  const [mileage, setMileage] = useState<string>('')

  const [error, setError] = useState<string | null>(null)

  // Reset form whenever the dialog (re-)opens or the seed changes.
  // Pattern A (You Might Not Need an Effect): snapshot the prior
  // (open, seed) tuple and in-render setState on the open transition
  // or a fresh seed.
  const [prevOpen, setPrevOpen] = useState(open)
  const [prevSeed, setPrevSeed] = useState(seed)
  if (open !== prevOpen || seed !== prevSeed) {
    setPrevOpen(open)
    setPrevSeed(seed)
    if (open) {
      setCustomerId(seed?.customer_id ?? '')
      setVin(seed?.vin ?? '')
      setYear(seed?.year != null ? String(seed.year) : '')
      setMake(seed?.make ?? '')
      setModel(seed?.model ?? '')
      setTrim(seed?.trim ?? '')
      setEngine(seed?.engine ?? '')
      setDrivetrain(seed?.drivetrain ?? '')
      setBody(seed?.body ?? '')
      setColor(seed?.color ?? '')
      setPlate(seed?.plate ?? '')
      setMileage(seed?.mileage != null ? String(seed.mileage) : '')
      setError(null)
    }
  }

  const customersQuery = useQuery({
    queryKey: ['customers'],
    enabled: isLoaded && open,
    queryFn: async (): Promise<CustomerOption[]> => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      const rows = (await res.json()) as Array<{ id: string; name: string }>
      return rows.map((r) => ({ id: r.id, name: r.name }))
    },
  })

  // Auto-decode VIN. Fires only on a complete 17-char value to avoid
  // partial-VIN flicker (see CLAUDE.md note on src/lib/vin.ts).
  // Pattern C (TanStack useQuery): isFetching becomes the "decoding"
  // indicator and error supplies the error string, so there's no
  // setState-in-effect kickoff. The success path still seeds the
  // editable fields when they're empty — done with a Pattern A
  // "reset state on data change" check in render against the last
  // decoded VIN we applied (so we don't keep re-seeding once the user
  // starts typing).
  const normalizedVin = vin.trim().toUpperCase()
  const vinComplete = normalizedVin.length === 17
  const vinValid = vinComplete && isValidVin(normalizedVin)
  const vinDecodeQuery = useQuery({
    queryKey: ['vin-decode', normalizedVin],
    enabled: vinValid,
    queryFn: ({ signal }) => decodeVin(normalizedVin, { signal }),
  })
  const vinDecoding = vinValid && vinDecodeQuery.isFetching
  const vinError = vinComplete && !vinValid
    ? t('form.vin_invalid')
    : vinValid && vinDecodeQuery.error
      ? vinDecodeQuery.error instanceof Error
        ? t('form.vin_decode_error')
        : null
      : null

  const [appliedDecodeVin, setAppliedDecodeVin] = useState<string | null>(null)
  if (
    vinValid &&
    vinDecodeQuery.data &&
    appliedDecodeVin !== normalizedVin
  ) {
    setAppliedDecodeVin(normalizedVin)
    const d = vinDecodeQuery.data
    // Don't clobber fields the user has already filled.
    if (!year && d.year) setYear(String(d.year))
    if (!make && d.make) setMake(d.make)
    if (!model && d.model) setModel(d.model)
    if (!engine && d.engine) setEngine(d.engine)
  }

  const mutation = useMutation({
    mutationFn: async (): Promise<SaveResponse> => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      // Owner is optional — orphan vehicles are valid when Emil hasn't
      // matched the customer yet. The row surfaces in /vehicles with a
      // "no owner assigned" hint and can be re-parented later.
      const trimmedVin = vin.trim().toUpperCase()
      if (trimmedVin && !isValidVin(trimmedVin)) {
        throw new Error(t('form.vin_invalid'))
      }

      const payload = {
        // Use `null` (not `undefined`) when no owner is selected so the
        // PATCH handler actually clears the FK. `undefined` would be
        // dropped from the JSON body and the worker treats a missing
        // `customer_id` as "leave it alone", which means picking
        // "No owner (assign later)" on an existing vehicle had no
        // effect. `null` flows through Zod's `.nullable()` and lands
        // as `updates.customerId = null`.
        customer_id: customerId ? customerId : null,
        vin: trimmedVin || null,
        year: year ? Number(year) : null,
        make: make.trim() || null,
        model: model.trim() || null,
        trim: trim.trim() || null,
        engine: engine.trim() || null,
        drivetrain: drivetrain.trim() || null,
        body: body.trim() || null,
        color: color.trim() || null,
        plate: plate.trim() || null,
        mileage: mileage ? Number(mileage) : null,
      }

      const url = editing ? `/api/vehicles/${seed!.id}` : '/api/vehicles'
      const method = editing ? 'PATCH' : 'POST'
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(text || t('form.error_submit'))
      }
      return (await res.json()) as SaveResponse
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      if (editing && seed?.id) {
        queryClient.invalidateQueries({ queryKey: ['vehicle', seed.id] })
      }
      // Customer detail page bakes vehicles into its query — bust it too
      // so the new/updated vehicle appears in the owner's profile.
      if (customerId) {
        queryClient.invalidateQueries({ queryKey: ['customer', customerId] })
      }
      onSaved?.(data.id)
      onOpenChange(false)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : String(err))
    },
  })

  const submitting = mutation.isPending
  // Owner is no longer a gate — orphan vehicles are allowed.
  const canSubmit = useMemo(() => !submitting, [submitting])

  const customerOptions = customersQuery.data ?? []

  return (
    <Dialog open={open} onOpenChange={(next) => (!submitting ? onOpenChange(next) : null)}>
      <DialogContent className="sm:max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('form.title_edit') : t('form.title_new')}
          </DialogTitle>
          <DialogDescription>
            {editing ? t('form.subtitle_edit') : t('form.subtitle_new')}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!canSubmit) return
            setError(null)
            mutation.mutate()
          }}
          className="space-y-5"
        >
          <section className="space-y-1.5">
            <Label htmlFor="vehicle-owner" className="flex items-baseline gap-2">
              {t('form.owner_label')}
              <span className="text-xs font-normal text-muted-foreground">
                {tCommon('optional')}
              </span>
            </Label>
            <select
              id="vehicle-owner"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              disabled={submitting || customersQuery.isLoading}
              className={SELECT_CLASS}
            >
              {/* The leading option is selectable here (no `disabled`)
                  so the user can intentionally save an orphan vehicle
                  by leaving owner unset. */}
              <option value="">
                {customersQuery.isLoading
                  ? tCommon('loading')
                  : t('form.owner_unassigned')}
              </option>
              {customerOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{t('form.owner_help')}</p>
          </section>

          <section className="space-y-3">
            <div className="text-[13px] font-semibold text-muted-foreground">
              {t('form.vin_section')}
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-vin">{t('form.vin_label')}</Label>
                <Input
                  id="vehicle-vin"
                  value={vin}
                  onChange={(e) =>
                    setVin(e.target.value.toUpperCase().replace(/\s+/g, ''))
                  }
                  placeholder={t('form.vin_placeholder')}
                  maxLength={17}
                  disabled={submitting}
                  className="font-mono"
                />
                {vinDecoding ? (
                  <p className="text-xs text-muted-foreground">
                    {t('form.vin_decoding')}
                  </p>
                ) : vinError ? (
                  <p className="text-xs text-destructive">{vinError}</p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-plate">{t('form.plate')}</Label>
                <Input
                  id="vehicle-plate"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  placeholder={t('form.plate_placeholder')}
                  disabled={submitting}
                  className="font-mono"
                />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <div className="text-[13px] font-semibold text-muted-foreground">
              {t('form.specs_section')}
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-year">{t('form.year')}</Label>
                <Input
                  id="vehicle-year"
                  type="number"
                  inputMode="numeric"
                  min={1900}
                  max={2100}
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="vehicle-make">{t('form.make')}</Label>
                <Input
                  id="vehicle-make"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  placeholder={t('form.make_placeholder')}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-mileage">{t('form.mileage')}</Label>
                <Input
                  id="vehicle-mileage"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={mileage}
                  onChange={(e) => setMileage(e.target.value)}
                  placeholder={t('form.mileage_placeholder')}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-model">{t('form.model')}</Label>
                <Input
                  id="vehicle-model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder={t('form.model_placeholder')}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-trim">{t('form.trim')}</Label>
                <Input
                  id="vehicle-trim"
                  value={trim}
                  onChange={(e) => setTrim(e.target.value)}
                  placeholder={t('form.trim_placeholder')}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-engine">{t('form.engine')}</Label>
                <Input
                  id="vehicle-engine"
                  value={engine}
                  onChange={(e) => setEngine(e.target.value)}
                  placeholder={t('form.engine_placeholder')}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-drivetrain">
                  {t('form.drivetrain')}
                </Label>
                <Input
                  id="vehicle-drivetrain"
                  value={drivetrain}
                  onChange={(e) => setDrivetrain(e.target.value)}
                  placeholder={t('form.drivetrain_placeholder')}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-body">{t('form.body')}</Label>
                <Input
                  id="vehicle-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t('form.body_placeholder')}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="vehicle-color">{t('form.color')}</Label>
                <Input
                  id="vehicle-color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder={t('form.color_placeholder')}
                  disabled={submitting}
                />
              </div>
            </div>
          </section>

          {error ? (
            <div className="rounded-[10px] border border-destructive/30 bg-destructive/10 p-2.5 text-xs text-destructive">
              {error}
            </div>
          ) : null}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={submitting}>
                {t('form.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('form.saving')}
                </>
              ) : editing ? (
                t('form.save_edit')
              ) : (
                t('form.save_new')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
